
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useVendorAssignment = () => {
  const [isAssigning, setIsAssigning] = useState(false);
  const { toast } = useToast();

  const assignVendorToService = async (serviceId: number, vendorId: string) => {
    setIsAssigning(true);
    
    try {
      console.log('Assigning vendor to service:', { serviceId, vendorId });
      
      // Update the service with the new owner
      const { data, error } = await supabase
        .from('services')
        .update({ owner_id: vendorId })
        .eq('id', serviceId)
        .select('*')
        .single();

      if (error) {
        console.error('Error assigning vendor:', error);
        throw error;
      }

      console.log('Vendor assigned successfully:', data);
      
      // The database trigger will automatically:
      // 1. Add the vendor role to the user
      // 2. Create notification preferences with service phone
      // 3. Log the assignment in test_runs
      
      toast({
        title: "Success",
        description: "Vendor assigned successfully. Notification preferences have been automatically configured.",
        variant: "success",
      });

      return data;
    } catch (error: any) {
      console.error('Failed to assign vendor:', error);
      toast({
        title: "Error",
        description: `Failed to assign vendor: ${error.message}`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsAssigning(false);
    }
  };

  const unassignVendorFromService = async (serviceId: number) => {
    setIsAssigning(true);
    
    try {
      console.log('Unassigning vendor from service:', serviceId);
      
      const { data, error } = await supabase
        .from('services')
        .update({ owner_id: null })
        .eq('id', serviceId)
        .select('*')
        .single();

      if (error) {
        console.error('Error unassigning vendor:', error);
        throw error;
      }

      console.log('Vendor unassigned successfully:', data);
      
      // The database trigger will automatically remove the vendor role
      // if the user no longer owns any services
      
      toast({
        title: "Success",
        description: "Vendor unassigned successfully.",
        variant: "success",
      });

      return data;
    } catch (error: any) {
      console.error('Failed to unassign vendor:', error);
      toast({
        title: "Error",
        description: `Failed to unassign vendor: ${error.message}`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsAssigning(false);
    }
  };

  return {
    assignVendorToService,
    unassignVendorFromService,
    isAssigning
  };
};
